import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Storefront, Lightning, Flame, ArrowSquareOut, Tag, CircleNotch } from "@phosphor-icons/react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function SponsoredCard({ item, onTrack }) {
  const isElectrical = item.category === "electrical";
  const typeLabel = { tool: "TOOL", equipment: "EQUIPMENT", material: "MATERIAL" }[item.type] || "PRODUCT";
  return (
    <div data-testid={`sponsored-${item.id}`} className="wp-card animate-slide-up">
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-sm flex items-center justify-center shrink-0 ${isElectrical ? "bg-[#002FA710]" : "bg-[#FF5A0010]"}`}>
          {isElectrical ? <Lightning size={20} className="text-[#002FA7]" /> : <Flame size={20} className="text-[#FF5A00]" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-0.5">
            <span className="font-mono-data text-[10px] px-1 py-0.5 bg-[#F4F4F5] rounded-sm text-[#404040]">{typeLabel}</span>
            <span className="font-mono-data text-[10px] text-[#404040]">by {item.sponsor}</span>
          </div>
          <h3 className="text-sm font-bold leading-snug" style={{ fontFamily: "'Chivo', sans-serif" }}>{item.name}</h3>
          <p className="text-[11px] text-[#404040] mt-0.5 leading-relaxed">{item.description}</p>
          <div className="flex items-center justify-between mt-2">
            {item.price && (
              <span className="font-mono font-bold text-sm text-[#0A0A0A]">{item.price}</span>
            )}
            <a
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => onTrack(item)}
              data-testid={`visit-sponsor-${item.id}`}
              className="text-[10px] font-bold text-[#002FA7] flex items-center gap-0.5 hover:underline"
            >
              Learn More <ArrowSquareOut size={10} />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SponsoredPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/sponsored`);
      setItems(res.data);
    } catch {
      toast.error("Failed to load sponsored items");
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const trackClick = async (item) => {
    try {
      // Use affiliate link endpoint for tracked redirect
      const res = await axios.get(`${API}/affiliate/${item.id}`);
      if (res.data.url) {
        window.open(res.data.url, '_blank');
      }
    } catch {
      // Fallback to direct URL
      await axios.post(`${API}/sponsored/click`, {
        item_id: item.id, item_name: item.name,
        sponsor: item.sponsor, url: item.url
      });
    }
  };

  const electrical = items.filter(i => i.category === "electrical");
  const hvac = items.filter(i => i.category === "hvac");

  return (
    <div className="p-4 max-w-lg mx-auto" data-testid="sponsored-page">
      <div className="flex items-center gap-2 mb-1">
        <Storefront size={20} className="text-[#002FA7]" />
        <h1 className="text-xl font-black tracking-tight" style={{ fontFamily: "'Chivo', sans-serif" }}>
          Pro Tools & Equipment
        </h1>
      </div>
      <p className="text-xs text-[#404040] mb-1 font-mono-data">INDUSTRY-RECOMMENDED PRODUCTS</p>
      <p className="text-[10px] text-[#404040] mb-4 italic">
        Featured products from trusted industry brands. Sponsored content is clearly labeled.
      </p>

      {loading ? (
        <div className="flex justify-center py-12"><CircleNotch size={28} className="animate-spin text-[#002FA7]" /></div>
      ) : (
        <Tabs defaultValue="all">
          <TabsList className="grid w-full grid-cols-3 h-9 mb-4">
            <TabsTrigger value="all" className="text-xs" data-testid="sponsored-all-tab">All</TabsTrigger>
            <TabsTrigger value="electrical" className="text-xs" data-testid="sponsored-electrical-tab">Electrical</TabsTrigger>
            <TabsTrigger value="hvac" className="text-xs" data-testid="sponsored-hvac-tab">HVAC</TabsTrigger>
          </TabsList>
          <TabsContent value="all">
            <div className="space-y-3">{items.map((i,idx) => <div key={i.id} style={{animationDelay:`${idx*0.05}s`}}><SponsoredCard item={i} onTrack={trackClick} /></div>)}</div>
          </TabsContent>
          <TabsContent value="electrical">
            <div className="space-y-3">{electrical.map((i,idx) => <div key={i.id} style={{animationDelay:`${idx*0.05}s`}}><SponsoredCard item={i} onTrack={trackClick} /></div>)}</div>
          </TabsContent>
          <TabsContent value="hvac">
            <div className="space-y-3">{hvac.map((i,idx) => <div key={i.id} style={{animationDelay:`${idx*0.05}s`}}><SponsoredCard item={i} onTrack={trackClick} /></div>)}</div>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
